import React, { useState, useEffect, useMemo } from 'react'
import {
  Button as MaterialButton,
  IconButton,
  TextField,
} from '@material-ui/core'
import { connect } from 'react-redux'
import CloseIcon from '@material-ui/icons/Close'
import cn from 'classnames'
import { v4 as uuidv4 } from 'uuid'
import Button from 'components/common/Button/Button'
import { injectIntl } from 'react-intl'
import { ReactComponent as PlusIcon } from 'assets/img/plus-icon.svg'
import { ReactComponent as Bin } from 'assets/img/bin.svg'
import SelectInput from 'components/common/Inputs/SelectInput'
import { ReactComponent as Edit } from 'assets/img/edit-icon-redesign.svg'
import { Flex, RedAsterisk } from 'components/common/styles'
import { fetchTriggerInputs } from 'reducers/jobsTriggers/reducer'
import { BUTTON_TYPE_SECONDARY } from 'components/common/Button/constants'
import { cloneDeep, isEmpty } from 'lodash'
import { List } from 'immutable'
import {
  StyledActionSection,
  StyledButtonHolder,
  StyledCondition,
  StyledModal,
  StyledModalBoxSection,
  StyledModalOverlay,
  StyledName,
  StyledRuleSettings,
  StyledFormSection,
} from 'components/common/Modals/styles'
import { fetchTaskInputsTree } from 'reducers/jobTasksAdmin/reducer'
import {
  LOGIC_CONDITIONS_OPTIONS,
  BOOLEAN_INPUT_OPTIONS,
  POPULATE_VALUE_OPTIONS,
} from 'constants/conditionalLogic'
import {
  ConditionalLogicContainer,
  ConditionalLogicHeader,
  ConditionRow,
  CriteriaContainer,
  Description,
  IconButtonContainer,
  IconsContainer,
  ModalTitle,
  Operator,
  SelectedConditionalBlock,
} from './ConditionalLogic.styles'
import {
  CRITERIA,
  FilteredInput,
  Input,
  OPERATORS,
  Option,
  PopulateLogicProps,
  PopulateValue,
  TaskInputsItem,
  TreeItem,
} from './ConditionalLogic.types'
import messages from './ConditionalLogic.messages'
import {
  findPopulateInputById,
  formatPopulationValueLabel,
  getDefaultPopulationValue,
  getPopulateInputsFromTree,
  getPopulateRows,
  getValidValue,
  isPopulationValueValid,
  mergePopulateInputOptions,
  parsePopulateSelectValue,
  stringifyPopulateSelectValue,
  getTriggerInputFromConditions,
} from './ConditionalLogic.utils'

const sanitizePopulateBlock = (block: PopulateValue): PopulateValue => {
  const validInputIds = (block.inputs || [])
    .map(inp =>
      inp === null || inp === undefined ? null : String(getValidValue(inp)),
    )
    .filter((id): id is string => !!id)
  const populationValues = Object.entries(block.population_values || {}).reduce(
    (acc, [key, value]) => {
      if (validInputIds.includes(key)) {
        acc[key] = Array.isArray(value) ? JSON.stringify(value) : String(value)
      }

      return acc
    },
    {} as Record<string, string>,
  )
  const triggerInput =
    getTriggerInputFromConditions(block.conditions) ?? block.trigger_input

  return {
    ...block,
    operator: OPERATORS.AND,
    conditions: block.conditions?.slice(0, 1) || [],
    trigger_input:
      triggerInput && typeof triggerInput === 'object'
        ? triggerInput.id
        : triggerInput,
    inputs: validInputIds,
    population_values: populationValues,
  }
}

export const PopulateLogicComponent = ({
  intl: { formatMessage },
  featureFlags,
  input,
  formData,
  fetchTriggerInputs,
  inputsList,
  isFetching,
  fetchTaskInputsTree,
  taskInputs,
  isInputsTreeFetching,
}: PopulateLogicProps) => {
  const populateLogicItems = Array.isArray(input.value) ? input.value : []
  const [isSidebarOpened, setIsSidebarOpened] = useState(false)
  const [isEdit, setIsEdit] = useState<number | null>(null)
  const [selectedPopulateLogic, setSelectedPopulateLogic] = useState<
    PopulateValue
  >({} as PopulateValue)
  const [populateRowKeys, setPopulateRowKeys] = useState<string[]>([])

  useEffect(() => {
    if (isSidebarOpened) {
      fetchTriggerInputs({ id: formData.workflow.id })
      fetchTaskInputsTree({ taskId: formData.id })

      return
    }

    if (!isSidebarOpened) {
      setIsEdit(null)
      setPopulateRowKeys([])
    }
  }, [isSidebarOpened])

  const toggleIsSidebarOpened = () => setIsSidebarOpened(!isSidebarOpened)

  const formatListWithTranslation = (
    list: { label: string; value: string | number }[],
  ) =>
    list.map(({ value, label }) => ({
      value,
      label:
        label === 'is'
          ? formatMessage(messages[label]).toLowerCase()
          : formatMessage(messages[label]),
    }))

  const formattedLogicConditions = {
    number: formatListWithTranslation(
      LOGIC_CONDITIONS_OPTIONS.filter(({ inputTypes }) =>
        inputTypes.includes('number'),
      ),
    ),
    other: formatListWithTranslation(
      LOGIC_CONDITIONS_OPTIONS.filter(
        ({ inputTypes }) => !inputTypes.includes('number'),
      ),
    ),
  }

  const filteredInputs = useMemo(
    () =>
      inputsList
        ?.toJS()
        .find((task: TaskInputsItem) => task.task_id === formData.id)
        ?.inputs?.filter((item: Input) =>
          ['select', 'boolean', 'number', 'string'].includes(
            item.type as string,
          ),
        )
        ?.map((item: Input) => ({
          value: item.id,
          label: item.title,
          options: item.options,
          type: item.type,
          multiple: Boolean(item.multiple),
        })) || [],
    [JSON.stringify(inputsList), JSON.stringify(formData)],
  )

  const isLoading = useMemo(() => isInputsTreeFetching || isFetching, [
    isInputsTreeFetching,
    isFetching,
  ])

  const taskInputsTree = useMemo(() => taskInputs.toArray(), [
    JSON.stringify(taskInputs),
  ])

  const populateInputs = useMemo(
    () =>
      mergePopulateInputOptions(
        getPopulateInputsFromTree(taskInputsTree),
        filteredInputs,
      ),
    [taskInputsTree, filteredInputs],
  )

  const formattedPopulateValues = formatListWithTranslation(
    POPULATE_VALUE_OPTIONS,
  )

  const resetPopulateRowKeys = (
    inputs?: (string | number | Input | null)[],
  ) => {
    setPopulateRowKeys(getPopulateRows(inputs).map(() => uuidv4()))
  }

  useEffect(() => {
    if (!isSidebarOpened) {
      return
    }

    const rowCount = getPopulateRows(selectedPopulateLogic?.inputs).length

    if (populateRowKeys.length !== rowCount) {
      resetPopulateRowKeys(selectedPopulateLogic?.inputs)
    }
  }, [isSidebarOpened, selectedPopulateLogic?.inputs, populateRowKeys.length])

  const getPopulateValueLabel = (inputId: string, value: string) =>
    formatPopulationValueLabel(
      findPopulateInputById(populateInputs, inputId),
      value,
      formatMessage,
    )

  const updatePopulateRowInput = (index: number, value: number | string) => {
    const newInput = { ...selectedPopulateLogic }
    const inputs = [...getPopulateRows(newInput.inputs)]
    const rowInput = inputs[index]
    const populationValues = { ...(newInput.population_values || {}) }

    if (rowInput) {
      const oldInputId = String(getValidValue(rowInput))

      if (oldInputId) {
        delete populationValues[oldInputId]
      }
    }

    inputs[index] = value
    const populateInput = findPopulateInputById(populateInputs, value)

    if (populateInput) {
      populationValues[String(value)] = getDefaultPopulationValue(populateInput)
    }

    newInput.inputs = inputs
    newInput.population_values = populationValues
    setSelectedPopulateLogic(newInput)
  }

  const updatePopulateRowValue = (
    inputId: string,
    value: string | string[],
    multiple?: boolean,
  ) => {
    const newInput = { ...selectedPopulateLogic }
    newInput.population_values = {
      ...(newInput.population_values || {}),
      [inputId]: stringifyPopulateSelectValue(value, multiple),
    }
    setSelectedPopulateLogic(newInput)
  }

  const removePopulateRow = (index: number) => {
    const newInput = { ...selectedPopulateLogic }
    const inputs = [...getPopulateRows(newInput.inputs)]
    const rowInput = inputs[index]
    const removedInputId = rowInput ? String(getValidValue(rowInput)) : null
    const populationValues = { ...(newInput.population_values || {}) }

    inputs.splice(index, 1)
    setPopulateRowKeys(prev => prev.filter((_, rowIndex) => rowIndex !== index))

    if (removedInputId) {
      delete populationValues[removedInputId]
    }

    newInput.inputs = inputs.length ? inputs : [null]
    newInput.population_values = populationValues

    if (!inputs.length) {
      setPopulateRowKeys([uuidv4()])
    }

    setSelectedPopulateLogic(newInput)
  }

  const addPopulateRow = () => {
    const newInput = { ...selectedPopulateLogic }
    newInput.inputs = [...getPopulateRows(newInput.inputs), null]
    setPopulateRowKeys(prev => [...prev, uuidv4()])
    setSelectedPopulateLogic(newInput)
  }

  const renderPopulateValueField = (
    populateInput: ReturnType<typeof findPopulateInputById>,
    inputId: string,
    isInputDisabled: boolean,
  ) => {
    const value = selectedPopulateLogic?.population_values?.[inputId] ?? ''

    if (!populateInput) {
      return null
    }

    if (populateInput.type === 'number' || populateInput.type === 'string') {
      return (
        <TextField
          disabled={isInputDisabled}
          required
          type={populateInput.type === 'number' ? 'number' : 'text'}
          value={value}
          onChange={({ target: { value: nextValue } }) => {
            updatePopulateRowValue(inputId, nextValue)
          }}
        />
      )
    }

    if (populateInput.type === 'select') {
      const selectValue = parsePopulateSelectValue(
        value,
        populateInput.multiple,
      )

      return (
        <SelectInput
          disabled={isInputDisabled}
          label={formatMessage(messages.chooseValue)}
          customWidth="212px"
          required
          multiple={populateInput.multiple}
          options={populateInput.options}
          value={selectValue}
          onChange={(nextValue: string | string[]) => {
            updatePopulateRowValue(inputId, nextValue, populateInput.multiple)
          }}
        />
      )
    }

    return (
      <SelectInput
        disabled={isInputDisabled}
        customWidth="85px"
        required
        options={formattedPopulateValues}
        value={value || 'true'}
        onChange={(nextValue: string) => {
          updatePopulateRowValue(inputId, nextValue)
        }}
      />
    )
  }

  const renderConditionCriteria = (criteria: CRITERIA): string => {
    switch (criteria) {
      case CRITERIA.IN:
      case CRITERIA.EQ:
        return ' ='
      case CRITERIA.LT:
        return ' <'
      case CRITERIA.LTE:
        return ' <='
      case CRITERIA.GT:
        return ' >'
      case CRITERIA.GTE:
        return ' >='
      case CRITERIA.NEQ:
      case CRITERIA.NOT_IN:
      default:
        return ' !='
    }
  }

  const isSaveDisabled = useMemo(() => {
    const hasInvalidConditions = selectedPopulateLogic?.conditions?.some(
      cond => !cond.input || isEmpty(cond.values),
    )

    if (hasInvalidConditions) {
      return true
    }

    const populateRows = getPopulateRows(selectedPopulateLogic?.inputs)
    const populationValues = selectedPopulateLogic?.population_values || {}

    return populateRows.some(rowInput => {
      if (rowInput === null || rowInput === undefined || rowInput === '') {
        return true
      }

      const inputId = String(getValidValue(rowInput))
      const populateInput = findPopulateInputById(populateInputs, inputId)
      const value = populationValues[inputId]

      return (
        !populateInput ||
        !isPopulationValueValid(
          populateInput.type,
          value,
          populateInput.multiple,
        )
      )
    })
  }, [JSON.stringify(selectedPopulateLogic), populateInputs])

  return (
    <ConditionalLogicContainer>
      <ConditionalLogicHeader>
        {formatMessage(messages.populateLogicBlock)}
      </ConditionalLogicHeader>
      <div>
        {populateLogicItems.map((item, index: number) => (
          <SelectedConditionalBlock
            key={item.uuid || uuidv4()}
            justify="space-between">
            <Flex direction="column" wrap="wrap">
              {(item.conditions || []).slice(0, 1).map(cond => {
                const currentInput = filteredInputs.find(
                  ({ value }: FilteredInput) => value === cond.input,
                )

                return (
                  <ConditionRow key={uuidv4()}>
                    <Flex>
                      <Operator>
                        {formatMessage(messages.ifCondition).toUpperCase()}
                      </Operator>
                      <Description>
                        &quot;
                        {cond.input_title || currentInput?.label}
                        &quot;
                      </Description>
                      <CriteriaContainer>
                        {renderConditionCriteria(cond.criteria)}
                      </CriteriaContainer>
                      <Description>
                        &quot;
                        {Array.isArray(cond.values)
                          ? cond.values.toString()
                          : Object.values(cond.values).toString()}
                        &quot;
                      </Description>
                    </Flex>
                  </ConditionRow>
                )
              })}
              <ConditionRow>
                <Flex>
                  <Operator>
                    {formatMessage(messages.then).toUpperCase()}
                  </Operator>
                  <ConditionRow marginLeft={8}>
                    &quot;
                    {formatMessage(messages.populate)}:{' '}
                    {item.inputs
                      ?.filter(
                        (inp): inp is string | number | Input =>
                          inp !== null && inp !== undefined,
                      )
                      .map(inp => {
                        const inputId = String(getValidValue(inp))
                        const populateInput = findPopulateInputById(
                          populateInputs,
                          inputId,
                        )
                        const title =
                          populateInput?.label ||
                          (typeof inp === 'object' ? inp.title : '')
                        const populationValue =
                          item.population_values?.[inputId]

                        return populationValue
                          ? `${title} = ${getPopulateValueLabel(
                              inputId,
                              populationValue,
                            )}`
                          : title
                      })
                      .join(', ')}
                    &quot;
                  </ConditionRow>
                </Flex>
              </ConditionRow>
            </Flex>
            <IconsContainer>
              <IconButton
                aria-label="edit populate rule"
                onClick={() => {
                  const block = cloneDeep(populateLogicItems[index])
                  setSelectedPopulateLogic(block)
                  resetPopulateRowKeys(block.inputs)
                  toggleIsSidebarOpened()
                  setIsEdit(index)
                }}>
                <Edit />
              </IconButton>
              <IconButton
                aria-label="delete populate rule"
                onClick={() => {
                  const newInput = [...populateLogicItems]
                  newInput.splice(index, 1)
                  input.onChange(newInput)
                }}>
                <Bin />
              </IconButton>
            </IconsContainer>
          </SelectedConditionalBlock>
        ))}
      </div>
      <MaterialButton
        onClick={() => {
          toggleIsSidebarOpened()
          setSelectedPopulateLogic({
            operator: OPERATORS.AND,
            conditions: [
              {
                criteria: CRITERIA.IN,
                input: null,
                values: [],
              },
            ],
            inputs: [null],
            population_values: {},
          })
          resetPopulateRowKeys([null])
        }}
        className="add-button">
        + {formatMessage(messages.newPopulateBlock)}
      </MaterialButton>
      <StyledModalOverlay
        className={cn({ closed: !isSidebarOpened })}
        onClick={toggleIsSidebarOpened}
      />
      <StyledModal
        customWidth="480px"
        opened={isSidebarOpened}
        direction="column"
        isNewHeader={featureFlags.APP_HEADER_V2}>
        <StyledFormSection>
          <StyledModalBoxSection>
            <ModalTitle align="center" justify="space-between">
              <div>{formatMessage(messages.populateLogicBlock)}</div>
              <IconButton size="small" onClick={toggleIsSidebarOpened}>
                <CloseIcon height="20px" />
              </IconButton>
            </ModalTitle>
          </StyledModalBoxSection>
          <StyledModalBoxSection>
            <StyledCondition align="center" wrap="wrap" marginBottom={10}>
              <StyledName>
                {formatMessage(messages.ifCondition).toUpperCase()}
              </StyledName>
              <Description>
                {formatMessage(messages.populateIfText)}
              </Description>
            </StyledCondition>
            {selectedPopulateLogic?.conditions
              ?.slice(0, 1)
              .map((item, index: number) => {
                const currentInput = filteredInputs.find(
                  ({ value }: FilteredInput) => value === item.input,
                )
                const isNumberInputSelected =
                  currentInput && currentInput.type === 'number'
                const selectCriteriaOptions = isNumberInputSelected
                  ? formattedLogicConditions.number
                  : formattedLogicConditions.other
                const conditionInputValueSelect = Array.isArray(item.values)
                  ? item.values
                  : Object.keys(item.values)
                const conditionInputValueNumber =
                  conditionInputValueSelect[0] || 0
                const isInputDisabled = !item.input || isLoading
                const chooseValueOptions =
                  currentInput?.options?.map(({ value, label }: Option) => ({
                    value,
                    label: typeof label === 'string' ? label : label.title,
                  })) || BOOLEAN_INPUT_OPTIONS

                return (
                  <StyledRuleSettings key={uuidv4()}>
                    <StyledCondition align="center" marginBottom={4}>
                      <StyledName width={45}>
                        {formatMessage(messages.input)}
                        <RedAsterisk>*</RedAsterisk>
                      </StyledName>
                      <SelectInput
                        disabled={isLoading}
                        label={formatMessage(messages.chooseInput)}
                        customWidth="305px"
                        required
                        options={filteredInputs}
                        value={item.input}
                        onChange={(value: string) => {
                          const newInput = { ...selectedPopulateLogic }
                          newInput.conditions[index].input = value
                          newInput.conditions[index].values = []
                          setSelectedPopulateLogic(newInput)
                        }}
                      />
                    </StyledCondition>
                    <StyledCondition align="center" marginBottom={16}>
                      {currentInput && (
                        <>
                          <StyledName width={45}>
                            {formatMessage(messages.value)}
                            <RedAsterisk>*</RedAsterisk>
                          </StyledName>
                          <SelectInput
                            disabled={isInputDisabled}
                            customWidth="85px"
                            required
                            options={selectCriteriaOptions}
                            value={item.criteria}
                            onChange={(value: CRITERIA) => {
                              const newInput = { ...selectedPopulateLogic }
                              newInput.conditions[index].criteria = value
                              setSelectedPopulateLogic(newInput)
                            }}
                          />
                          {isNumberInputSelected ? (
                            <TextField
                              disabled={isInputDisabled}
                              required
                              value={conditionInputValueNumber}
                              onChange={({ target: { value } }) => {
                                const newInput = { ...selectedPopulateLogic }
                                newInput.conditions[index].values = [value]
                                setSelectedPopulateLogic(newInput)
                              }}
                            />
                          ) : (
                            <SelectInput
                              disabled={isInputDisabled}
                              label={formatMessage(messages.chooseValue)}
                              multiple
                              customWidth="212px"
                              required
                              options={chooseValueOptions}
                              value={conditionInputValueSelect}
                              onChange={(value: string[]) => {
                                const newInput = { ...selectedPopulateLogic }
                                newInput.conditions[index].values = value
                                setSelectedPopulateLogic(newInput)
                              }}
                            />
                          )}
                        </>
                      )}
                    </StyledCondition>
                  </StyledRuleSettings>
                )
              })}
          </StyledModalBoxSection>
          <StyledModalBoxSection noBorder>
            <StyledCondition align="center" wrap="wrap" marginBottom={10}>
              <StyledName>
                {formatMessage(messages.then).toUpperCase()}
              </StyledName>
              <Description>
                {formatMessage(messages.populateThenText)}
              </Description>
            </StyledCondition>
            <StyledRuleSettings>
              {getPopulateRows(selectedPopulateLogic?.inputs).map(
                (rowInput, index: number) => {
                  const inputId = rowInput
                    ? String(getValidValue(rowInput))
                    : null
                  const currentPopulateInput = findPopulateInputById(
                    populateInputs,
                    inputId,
                  )
                  const selectedInputIds = getPopulateRows(
                    selectedPopulateLogic?.inputs,
                  )
                    .map(row => (row ? String(getValidValue(row)) : null))
                    .filter(Boolean)
                  const availablePopulateOptions = populateInputs.filter(
                    ({ value }) =>
                      !selectedInputIds.includes(String(value)) ||
                      String(value) === inputId,
                  )
                  const populateRows = getPopulateRows(
                    selectedPopulateLogic?.inputs,
                  )
                  const isInputDisabled = isLoading

                  return (
                    <StyledRuleSettings key={populateRowKeys[index]}>
                      <StyledCondition align="center" marginBottom={4}>
                        <StyledName width={45}>
                          {formatMessage(messages.input)}
                          <RedAsterisk>*</RedAsterisk>
                        </StyledName>
                        <SelectInput
                          disabled={isInputDisabled}
                          label={formatMessage(messages.chooseInput)}
                          customWidth="305px"
                          required
                          options={availablePopulateOptions}
                          value={
                            rowInput ? Number(getValidValue(rowInput)) : null
                          }
                          onChange={(value: number | string) => {
                            updatePopulateRowInput(index, value)
                          }}
                        />
                        {populateRows.length > 1 && (
                          <IconButtonContainer>
                            <IconButton
                              onClick={() => removePopulateRow(index)}>
                              <Bin />
                            </IconButton>
                          </IconButtonContainer>
                        )}
                      </StyledCondition>
                      {currentPopulateInput && inputId && (
                        <StyledCondition align="center" marginBottom={16}>
                          <StyledName width={45}>
                            {formatMessage(messages.value)}
                            <RedAsterisk>*</RedAsterisk>
                          </StyledName>
                          {renderPopulateValueField(
                            currentPopulateInput,
                            inputId,
                            isInputDisabled,
                          )}
                        </StyledCondition>
                      )}
                      {index + 1 === populateRows.length ? (
                        <StyledCondition align="center">
                          <Button
                            startIcon={<PlusIcon />}
                            onClick={addPopulateRow}
                            type={BUTTON_TYPE_SECONDARY}>
                            {formatMessage(messages.addPopulate)}
                          </Button>
                        </StyledCondition>
                      ) : (
                        <hr />
                      )}
                    </StyledRuleSettings>
                  )
                },
              )}
            </StyledRuleSettings>
          </StyledModalBoxSection>
        </StyledFormSection>
        <StyledActionSection align="center">
          <StyledButtonHolder>
            <Button
              type="primary"
              disabled={isLoading || isSaveDisabled}
              onClick={() => {
                const newInput = [...populateLogicItems]
                const blockToSave = sanitizePopulateBlock(selectedPopulateLogic)

                if (isEdit === null) {
                  input.onChange([...newInput, blockToSave])
                } else {
                  newInput[isEdit] = blockToSave
                  input.onChange(newInput)
                }

                toggleIsSidebarOpened()
              }}>
              {formatMessage(messages.save)}
            </Button>
            <Button type="secondary" onClick={() => toggleIsSidebarOpened()}>
              {formatMessage(messages.cancel)}
            </Button>
          </StyledButtonHolder>
        </StyledActionSection>
      </StyledModal>
    </ConditionalLogicContainer>
  )
}

export default connect(
  (state: {
    getIn: (
      path: Array<string>,
    ) =>
      | Record<string, boolean>
      | boolean
      | List<TaskInputsItem>
      | List<TreeItem>
  }) => ({
    featureFlags: state.getIn(['profile', 'profile', 'featureFlags']) as Record<
      string,
      boolean
    >,
    inputsList: state.getIn(['jobsTriggers', 'triggerInputs']) as List<
      TaskInputsItem
    >,
    isFetching: state.getIn(['jobsTriggers', 'isFetching']) as boolean,
    taskInputs: state.getIn(['jobTasksAdmin', 'taskInputsTree']) as List<
      TreeItem
    >,
    isInputsTreeFetching: state.getIn([
      'jobTasksAdmin',
      'isInputsTreeFetching',
    ]) as boolean,
  }),
  {
    fetchTriggerInputs,
    fetchTaskInputsTree,
  },
)(injectIntl(PopulateLogicComponent))
